import type { BaseTexture } from '@babylonjs/core/Materials/Textures/baseTexture';
import type { Matrix} from '@babylonjs/core/Maths/math.vector';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { InstancedLinesMesh, LinesMesh } from '@babylonjs/core/Meshes/linesMesh';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { PBRMaterial as BJSPBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { Vector2, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { VertexBuffer } from '@babylonjs/core/Buffers/buffer';

import type { MaterialManager, SceneElement } from '@egr/wcf/modules/core/mdl';
import { BoundingBox, PbrMaterial } from '@egr/wcf/modules/core/mdl';
import { EPSILON } from '@egr/wcf/modules/utils/math';
import { Texture as mdlTexture } from '@egr/wcf/modules/core/mdl/Texture';
import { delayForAnimation } from '@egr/wcf/modules/utils/async';

const NumFormat = new Intl.NumberFormat('en-US', { useGrouping: false, minimumFractionDigits: 0, maximumFractionDigits: 6 });

export interface UsdData {
    usda: string;
    textureFiles: Map<string, ArrayBuffer>;
}

export async function exportToUsd(
    pMatManager: MaterialManager,
    pSceneElements: ReadonlyArray<SceneElement>,
    pScaleFactor: number
): Promise<UsdData> {
    //get root nodes and center offset
    const tRootNodes = new Array<TransformNode>();
    for (const tElement of pSceneElements) {
        tRootNodes.push(tElement.node);
    }
    let tCenterOffset = new Vector3(0, 0, 0);
    const tVisibleBounds = BoundingBox.FromElements(pSceneElements, true, true);
    if (tVisibleBounds.isValid()) {
        tCenterOffset = tVisibleBounds.getCenter();
        tCenterOffset.scaleInPlace(-1);
        tCenterOffset.y = 0; //keep height
    }

    const tUsedMaterials = new Set<PbrMaterial>();
    const tUsedTextureFiles = new Map<string, ArrayBuffer>();
    const tNodes = await convertRootNodes(tRootNodes, tCenterOffset, pScaleFactor, tUsedMaterials);
    const tMaterials = await convertMaterials(pMatManager, tUsedMaterials, tUsedTextureFiles);
    const tUsda =
        `#usda 1.0
(
upAxis = "Y"
metersPerUnit = 1
)
${tNodes}
${tMaterials}`;

    return {
        usda: tUsda,
        textureFiles: tUsedTextureFiles
    };
}

async function convertMeshes(
    pNode: TransformNode,
    pCenterOffset: Vector3,
    pScaleFactor: number,
    pUsedMaterials: Set<PbrMaterial>,
    pResults: Array<string>
): Promise<void> {
    if (
        pNode instanceof Mesh && pNode.isVisible &&
        !(pNode instanceof LinesMesh || pNode instanceof InstancedLinesMesh)
    ) {
        await delayForAnimation();

        const tMat = pNode.material instanceof PbrMaterial ? pNode.material : null;
        if (tMat != null) {
            pUsedMaterials.add(tMat);
        }

        pResults.push(
`\ndef Mesh "mesh${pNode.uniqueId}"
{
${getFaceVertexCount(pNode)}
${getIndices(pNode)}
${getVertices(pNode, pCenterOffset, pScaleFactor)}
${getNormals(pNode)}
${getUV(pNode)}
${tMat != null ? `rel material:binding = </Materials/mat${tMat.uniqueId}>` : ''}
}`);
    }

    const tChilds = pNode.getChildTransformNodes(true);
    for (const tChild of tChilds) {
        await convertMeshes(tChild, pCenterOffset, pScaleFactor, pUsedMaterials, pResults);
    }
}

async function convertRootNodes(
    pRootNodes: Array<TransformNode>,
    pCenterOffset: Vector3,
    pScaleFactor: number,
    pUsedMaterials: Set<PbrMaterial>
): Promise<string> {
    const tRootStrings = new Array<string>();

    for (let i = 0; i < pRootNodes.length; ++i) {
        const tNodeStrings = new Array<string>();
        await convertMeshes(pRootNodes[i], pCenterOffset, pScaleFactor, pUsedMaterials, tNodeStrings);
        tRootStrings.push(`\ndef "root${i}" {\n${tNodeStrings.join('')}\n}`);
    }

    return tRootStrings.join('');
}

async function convertMaterials(
    pMatManager: MaterialManager,
    pMaterials: Set<PbrMaterial>,
    pUsedTextureFiles: Map<string, ArrayBuffer>
): Promise<string> {
    const tMatStrings = new Array<string>();

    const tValues = pMaterials.values();

    let tNext = tValues.next();

    while (!tNext.done) {
        await delayForAnimation();
        tMatStrings.push(convertMaterial(pMatManager, tNext.value, pUsedTextureFiles));
        tNext = tValues.next();
    }

    return (`def "Materials" {${tMatStrings.join('')}\n}`);
}

function convertVec3(pV: Vector3): string {
    return (`(${NumFormat.format(pV.x)},${NumFormat.format(pV.y)},${NumFormat.format(pV.z)})`);
}

function getVertices(
    pMesh: Mesh,
    pCenterOffset: Vector3,
    pScaleFactor: number
): string {
    const tVertices = pMesh.getVerticesData(VertexBuffer.PositionKind);
    const tPointStrings = new Array<string>();
    if (tVertices != null) {
        const tWM = pMesh.getWorldMatrix();
        const tV = new Vector3();
        for (let i = 0; i < tVertices.length; i += 3) {
            Vector3.FromArrayToRef(tVertices, i, tV);
            Vector3.TransformCoordinatesToRef(tV, tWM, tV);
            tV.addInPlace(pCenterOffset);
            tV.scaleInPlace(pScaleFactor);
            tV.z = -tV.z; //convert to right handed
            tPointStrings.push(convertVec3(tV));
        }
    }
    return `point3f[] points = [${tPointStrings.join(',')}]`;
}

function getUV(pMesh: Mesh): string {
    if (pMesh.material == null || !(pMesh.material instanceof BJSPBRMaterial))
        return ('');
    const tTm = getTextureMatrix(pMesh.material);
    if (tTm == null)
        return ('');

    const tUVs = pMesh.getVerticesData(VertexBuffer.UVKind);
    if (tUVs != null) {
        const tUVStrings = new Array<string>();
        const tV: Vector2 = Vector2.Zero();
        for (let i = 0; i < tUVs.length; i += 2) {
            Vector2.FromArrayToRef(tUVs, i, tV);
            Vector2.TransformToRef(tV, tTm, tV);
            tUVStrings.push(`(${NumFormat.format(tV.x)},${NumFormat.format(tV.y)})`);
        }
        return `texCoord2f[] primvars:st = [${tUVStrings.join(',')}] ( interpolation = "varying" )\n`;
    }
    return ('');
}

function getTextureMatrix(pMat: BJSPBRMaterial): Matrix | null {
    if (pMat.albedoTexture != null)
        return (pMat.albedoTexture.getTextureMatrix());
    if (pMat.microSurfaceTexture != null)
        return (pMat.microSurfaceTexture.getTextureMatrix());
    if (pMat.metallicTexture != null)
        return (pMat.metallicTexture.getTextureMatrix());
    if (pMat.bumpTexture != null)
        return (pMat.bumpTexture.getTextureMatrix());
    return (null);
}

function getIndices(pMesh: Mesh): string {
    const tWM = pMesh.getWorldMatrix();
    const tIsMirrored = tWM.determinant() < 0;
    const tIndices = pMesh.getIndices(undefined, tIsMirrored);

    //flip triangles for mirrored meshes
    if (tIndices != null && tIsMirrored) {
        for (let i = 0; i < tIndices.length; i += 3) {
            const t = tIndices[i + 1];
            tIndices[i + 1] = tIndices[i + 2];
            tIndices[i + 2] = t;
        }
    }
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    return (`int[] faceVertexIndices=[${tIndices}]`);
}

function getFaceVertexCount(pMesh: Mesh): string {
    const tArray = new Array<number>(pMesh.getTotalIndices() / 3);
    tArray.fill(3);
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    return (`int[] faceVertexCounts=[${tArray}]`);
}

function getNormals(pMesh: Mesh): string {
    const tNormals = pMesh.getVerticesData(VertexBuffer.NormalKind);
    const tNormalStrings = new Array<string>();
    if (tNormals != null) {
        const tWM = pMesh.getWorldMatrix();
        const tN = new Vector3();
        for (let i = 0; i < tNormals.length; i += 3) {
            Vector3.FromArrayToRef(tNormals, i, tN);
            Vector3.TransformNormalToRef(tN, tWM, tN);
            tN.normalize();
            tN.z = -tN.z; //convert to right handed
            tNormalStrings.push(convertVec3(tN));
        }
    }
    return `normal3f[] normals = [${tNormalStrings.join(',')}]( interpolation = "varying" )`;
}

function convertMaterial(
    pMatManager: MaterialManager,
    pMaterial: PbrMaterial,
    pUsedTextureFiles: Map<string, ArrayBuffer>
): string {
    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    const tId = 'mat' + pMaterial.uniqueId;
    let tDiffuseColor = `color3f inputs:diffuseColor = (${pMaterial.albedoColor.asArray().join()})`;
    let tOpacity = `float inputs:opacity = ${pMaterial.alpha}`;
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    let tMetallic = `float inputs:metallic = ${pMaterial.metallic}`;
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    let tRoughness = `float inputs:roughness = ${pMaterial.roughness}`;
    let tStReader: string = '';

    const tAlbedoTexFilename = prepareTexture(pMatManager, pMaterial.albedoTexture, pUsedTextureFiles, (pTex) =>
    {
        tDiffuseColor = `color3f inputs:diffuseColor.connect = </Materials/${tId}/diffuseTexture.outputs:rgb>`;

        //use alpha channel
        if (pTex.hasAlpha) {
            tOpacity = `float inputs:opacity.connect = </Materials/${tId}/diffuseTexture.outputs:a>`;
        }
    });

    const tMetallicTexFilename = prepareTexture(pMatManager, pMaterial.metallicTexture, pUsedTextureFiles, () =>
    {
        tMetallic = `float inputs:metallic.connect = </Materials/${tId}/metallicTexture.outputs:rgb>`;
    });

    const tRoughnessTexFilename = prepareTexture(pMatManager, pMaterial.microSurfaceTexture, pUsedTextureFiles, () =>
    {
        tRoughness = `float inputs:roughness.connect = </Materials/${tId}/roughnessTexture.outputs:rgb>`;
    });

    const tBumpTexFilename = bumpTexSupported(pMaterial.albedoTexture, pMaterial.bumpTexture) ?
        prepareTexture(pMatManager, pMaterial.bumpTexture, pUsedTextureFiles) : undefined;

    if (tAlbedoTexFilename != null || tMetallicTexFilename != null || tRoughnessTexFilename != null || tBumpTexFilename != null) {
        tStReader =
`
def Shader "stReader"
{
    uniform token info:id = "UsdPrimvarReader_float2"
    token inputs:varname.connect = </Materials/${tId}.inputs:frame:stPrimvarName>
    float2 outputs:result
}
`;
    }

    return (
`\ndef Material "${tId}"
{
token outputs:surface.connect = </Materials/${tId}/PBRShader.outputs:surface>
${tBumpTexFilename != null ? `token outputs:displacement.connect = </Materials/${tId}/PBRShader.outputs:displacement>` : ''}
def Shader "PBRShader"
{
    uniform token info:id = "UsdPreviewSurface"
    ${tDiffuseColor}
    ${tOpacity}
    ${tMetallic}
    ${tRoughness}
    token outputs:surface\
    ${tBumpTexFilename != null ? `\ntoken outputs:displacement\nnormal3f inputs:normal.connect=</Materials/${tId}/normalTexture.outputs:rgb>` : ''}

}
${tStReader}
${defineTexture(tId, 'diffuseTexture', tAlbedoTexFilename)}
${defineTexture(tId, 'metallicTexture', tMetallicTexFilename)}
${defineTexture(tId, 'roughnessTexture', tRoughnessTexFilename)}
${defineTexture(tId, 'normalTexture', tBumpTexFilename)}
}`
    );
}

function defineTexture(pMatId: string, pName: string, pFilename: string | undefined): string
{
    if (pFilename == null)
        return ('');

    return (
`
def Shader "${pName}"
{
    uniform token info:id = "UsdUVTexture"
    asset inputs:file = @${pFilename}@
    float2 inputs:st.connect = </Materials/${pMatId}/stReader.outputs:result>
    float3 outputs:rgb
    float outputs:a
}
`
    );
}

function prepareTexture(
    pMatManager: MaterialManager,
    pTex: BaseTexture | null,
    pUsedTextureFiles: Map<string, ArrayBuffer>,
    pCallback?: (pTex: mdlTexture) => void
): string | undefined {
    if (pTex instanceof mdlTexture) {
        const tResData = pMatManager.getResourceTextureData(pTex.id);
        if (tResData?.data != null) {
            const tFilename = `textures/${pTex.id}.${tResData.type}`;

            pUsedTextureFiles.set(tFilename, tResData.data);

            if (pCallback != null) {
                pCallback(pTex);
            }

            return (tFilename);
        }
    }

    return (undefined);
}

function bumpTexSupported(
    pAlbedoTex: BaseTexture | null,
    pBumpTex: BaseTexture | null
): boolean {
    if (pBumpTex == null)
        return (false);
    if (pAlbedoTex == null)
        return (true);

    //both have to use the same texture matrix
    const tM1 = pAlbedoTex.getTextureMatrix();
    const tM2 = pBumpTex.getTextureMatrix();

    for (let i = 0; i < 4; ++i) {
        const tR1 = tM1.getRow(i);
        const tR2 = tM2.getRow(i);
        if (tR1 == null || tR2 == null)
            return (false);
        if (!tR1.equalsWithEpsilon(tR2, EPSILON))
            return (false);
    }
    return (true);
}